import {Injectable} from "@angular/core";
import {Apollo, ApolloQueryObservable} from "apollo-angular";
import gql from "graphql-tag";
import {Barcode} from "../model/barcode.interface";
import {Observable} from "rxjs/Observable";
import {VisbosService} from "../../core/visbos-core/graphql/visbos.service";
import {NgRedux, select} from "@angular-redux/store";
import {IAppState} from "../../shared/redux-store";
import {SessionActions} from "../../shared/actions";
import {ApolloQueryResult} from "apollo-client";
import {map} from "rxjs/operators";

export const fragments = {
  barcode: gql`
    fragment BarcodeFields on Barcode {
      cuid, label, ean, ean_type,
      identifiable {
        cuid, __typename,
        ...on Stock {
          act_stock extraction_amount registration_required
        }
        ...on Asset {
          created_at, updated_at, deleted_at
        }
      }
    }
  `
};

export const resolveBarcodeQuery = gql`
  query ResolveBarcodeQuery($cuid: String, $ean: String, $withTrashed: Boolean) {
    barcode(cuid: $cuid, ean: $ean, withTrashed: $withTrashed) {
      ...BarcodeFields
    }
  }

  ${fragments.barcode}
`;

export const fetchBarcodesQuery = gql`
  query FetchBarcodesForAdminUnitQuery($AU: String!, $withTrashed: Boolean) {
    barcodes(administration_unit_cuid: $AU, withTrashed: $withTrashed) {
      ...BarcodeFields
    }
  }

  ${fragments.barcode}
`;

export const generateBarcodesPdfQuery = gql`
  query GenerateBarcodeStickersPdf($barcodeCuids: [String], $identifiableCuids: [String], $printedSheetCuid: String,
    $startRow: Int, $startColumn: Int) {
    generateBarcodeStickersPDF(barcodeCuids: $barcodeCuids, identifiableCuids: $identifiableCuids, printedSheetCuid: $printedSheetCuid,
      startRow: $startRow, startColumn: $startColumn)
  }
`;

export const GenerateAccountUnitBarcodesPdfMutation = gql`
  mutation GenerateAccountUnitBarcodesPdf($cuids: [String]) {
    generateAccountingUnitBarcodesPDF(identifiable_cuids: $cuids)
  }
`;

export interface GenerateBarcodeStickersPdfResponse {
  generateBarcodeStickersPDF: string;
}

export interface FetchBarcodeResponse {
  barcode: Barcode;
}

export interface FetchBarcodesResponse {
  barcodes: Barcode[];
}

@Injectable()
export class BarcodeService extends VisbosService {

  @select(['session', 'activeAdminUnit'])
  activeAdminUnit$: Observable<string>;


  constructor(apollo: Apollo, ngRedux: NgRedux<IAppState>, sessionActions: SessionActions) {
    super(apollo, ngRedux, sessionActions);
  }

  public getGeneratedBarcode(): Observable<Barcode> {

    let query = this.mutate({
      mutation: gql`
        mutation Barcode {
          generateBarcode {
            cuid, ean, ean_type
          }
        }`
    });

    return query.pipe(
      map<any, Barcode>(({data}: any) => data.generateBarcode)
    );

  }

  public fetchBarcodes(adminUnitCuid?: string | Observable<string>, withTrashed?: boolean) {

    let query = this.fetchItems<FetchBarcodesResponse>({
      query: fetchBarcodesQuery,
      variables: {
        AU: adminUnitCuid ? adminUnitCuid : this.activeAdminUnit$,
        withTrashed: withTrashed
      }
    });

    return query;

  }

  public cacheBarcodes(adminUnitCuid?: string | Observable<string>, withTrashed?: boolean) {

    let query = this.fetchItems<FetchBarcodesResponse>({
      query: fetchBarcodesQuery,
      variables: {
        AU: adminUnitCuid ? adminUnitCuid : this.activeAdminUnit$,
        withTrashed: withTrashed
      },
      pollInterval: 60000, // Re-cache every 60 seconds
    });

    return query;

  }

  public checkBarcode(ean: string, stockCuid?: string | Observable<string>): ApolloQueryObservable<boolean> {

    let query = this.fetchItems({
      query: gql`
        query CheckEAN($ean: String!, $cuid: String) {
          checkAndReserveBarcode(ean: $ean, cuid: $cuid)
        }
      `,
      variables: {
        ean: ean,
        cuid: stockCuid
      }
    });

    return query.pipe(
      map(({data}: any) => data.checkAndReserveBarcode)
    ) as ApolloQueryObservable<boolean>;

  }

  public resolveEAN(ean: string, withTrashed?: boolean): Observable<ApolloQueryResult<FetchBarcodeResponse>> {

    return this.apollo.query<FetchBarcodeResponse>({
      query: resolveBarcodeQuery,
      variables: {
        ean: ean,
        cuid: null,
        withTrashed: withTrashed
      },
      fetchPolicy: "cache-first"
    });

  }

  public generateBarcodeStickersPdf(barcodeCuids: string[] = null, identifiableCuids: string[] = null, printedSheetCuid: string, startRow: number = 1, startColumn: number = 1): ApolloQueryObservable<GenerateBarcodeStickersPdfResponse> {
    return this.fetchItems({
      query: generateBarcodesPdfQuery,
      variables: {
        barcodeCuids: barcodeCuids,
        identifiableCuids: identifiableCuids,
        printedSheetCuid: printedSheetCuid,
        startRow: startRow,
        startColumn: startColumn
      },
      fetchPolicy: "cache-first"
    })
  }

  public generateAccountingUnitBarcodesPdf(accountingUnitCuids: string[]): Observable<any> {
    return this.mutate({
      mutation: GenerateAccountUnitBarcodesPdfMutation,
      variables: {
        cuids: accountingUnitCuids
      }
    });
  }

}



// WEBPACK FOOTER //
// ./src/app/identity/services/barcode.service.ts